import { cloneDeep } from '@apollo/client/utilities';
import { SystemBin } from '../bin';
import { EntityInput, forCreate, forDelete, forUpdate } from '../common/entity';
import { CustomField, CustomFieldEntityType } from '../custom_field';
import { SystemStockLocation } from '../stock_location/stockLocation';

import { Container } from './container';

export class ContainerInput extends Container implements EntityInput<Container> {
  forCreate(): CreateContainerInput {
    this.stockLocationId ||= SystemStockLocation.id;
    this.binId ||= SystemBin.id;
    return CreateContainerInput.from({ ...this, customFields: this.customFieldValues() }, CreateContainerInput);
  }

  forUpdate(): UpdateContainerInput {
    return UpdateContainerInput.from({ ...this, customFields: this.customFieldValues() }, UpdateContainerInput);
  }

  forDelete(): DeleteContainerInput {
    return DeleteContainerInput.from(this, DeleteContainerInput);
  }

  hasChanges(original: Container | undefined) {
    if (!original) return true;

    if (this.identifier !== original.identifier) return true;
    if (this.containerTypeId !== original.containerTypeId) return true;
    if (this.deleteOnOutbound !== original.deleteOnOutbound) return true;
    if (this.customFields.size !== original.customFields.size) return true;
    for (const [id, value] of this.customFields) {
      if (original.customFields.get(id)?.value !== value.value) return true;
    }

    return false;
  }

  validate(customFields: Map<string, CustomField>) {
    if (!this.identifier) return false;
    if (!this.containerTypeId) return false;

    for (const [id, customField] of customFields) {
      if (!customField.mandatory || customField.entityType !== CustomFieldEntityType.container) continue;

      if (!this.customFields.get(id)?.value) return false;
    }

    if (this.stockLocationId || this.binId) return !!this.stockLocationId && !!this.binId;

    return true;
  }

  withStockLocationId(stockLocationId: string | undefined): this {
    this.stockLocationId = stockLocationId || '';
    this.binId = '';
    this.parentId = undefined;

    return cloneDeep(this);
  }

  withContainerTypeId(containerTypeId?: string) {
    this.containerTypeId = containerTypeId || '';
    return cloneDeep(this);
  }

  withIdentifier(identifier: string) {
    this.identifier = identifier;
    return cloneDeep(this);
  }

  withParentId(parentId: string | undefined) {
    this.parentId = parentId;
    return cloneDeep(this);
  }

  withBinId(binId: string) {
    this.binId = binId;
    this.parentId = undefined;
    return cloneDeep(this);
  }

  withDeleteOnOutbound(deleteOnOutbound: boolean) {
    this.deleteOnOutbound = deleteOnOutbound;
    return cloneDeep(this);
  }
}

export class CreateContainerInput extends forCreate(Container) {}

export class UpdateContainerInput extends forUpdate(Container) {}

export class DeleteContainerInput extends forDelete(Container) {}
